import { useQuery, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import type { ReferralLetterView, SickSheetView } from '@wodi/shared';
import { Button, LoadingBlock, Notice } from '@wodi/ui';
import type { ReactNode } from 'react';

import { ApiError } from '../api/client.ts';
import {
  documentKeys,
  patientDocumentsQuery,
  patientReferralsQuery,
  patientSickSheetsQuery,
  visitReferralsQuery,
  visitSickSheetsQuery,
} from '../api/clinical-documents.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from './documents.module.css';
import { IssuedDocumentList } from './issued-list.tsx';
import { WriteReferral } from './referrals.tsx';
import { IssueSickSheet, type DiagnosisChoice } from './sick-sheets.tsx';
import { DocumentList, UploadDocument } from './uploads.tsx';

// Clinical documents in the consultation workspace and on the patient record
// (docs/plans/phase1-consultation.md §7.4): sick sheets and referral letters issued, and
// documents uploaded. What someone may do comes in as props, decided from their session
// by the page that mounts this; the server checks every action again.

export interface DocumentPermissions {
  /** clinical.read: see issued sick sheets and referral letters. */
  readIssued: boolean;
  issueSickSheet: boolean;
  writeReferral: boolean;
  readDocuments: boolean;
  uploadDocuments: boolean;
}

function Loaded<T>({
  query,
  label,
  children,
}: {
  query: UseQueryResult<T>;
  label: string;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <LoadingBlock label={label} />;
  if (query.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void query.refetch()}>Try again</Button>}>
        {query.error instanceof ApiError
          ? query.error.message
          : 'This list could not load. Try again.'}
      </Notice>
    );
  }
  return <>{children(query.data)}</>;
}

function IssuedSection({
  heading,
  sickSheetsKey,
  referralsKey,
  sickSheets,
  referrals,
  actions,
  may,
  online,
}: {
  heading: string;
  sickSheetsKey: QueryKey;
  referralsKey: QueryKey;
  sickSheets: UseQueryResult<{ items: SickSheetView[] }>;
  referrals: UseQueryResult<{ items: ReferralLetterView[] }>;
  actions: ReactNode;
  may: DocumentPermissions;
  online: boolean;
}) {
  return (
    <section className={styles.panel} aria-label={heading}>
      <h3 className={styles.subheading}>{heading}</h3>
      {actions}
      <Loaded query={sickSheets} label="Loading sick sheets">
        {(sheets) => (
          <Loaded query={referrals} label="Loading referral letters">
            {(letters) => (
              <IssuedDocumentList
                sickSheets={sheets.items}
                referrals={letters.items}
                listKeys={[sickSheetsKey, referralsKey]}
                maySickSheets={may.issueSickSheet}
                mayReferrals={may.writeReferral}
                online={online}
              />
            )}
          </Loaded>
        )}
      </Loaded>
    </section>
  );
}

function DocumentsSection({
  patientId,
  uploadVisitId,
  may,
  online,
}: {
  patientId: string;
  uploadVisitId: string | null;
  may: DocumentPermissions;
  online: boolean;
}) {
  const key = documentKeys.patientDocuments(patientId);
  const documents = useQuery({ ...patientDocumentsQuery(patientId), enabled: may.readDocuments });
  return (
    <section className={styles.panel} aria-label="Uploaded documents">
      <h3 className={styles.subheading}>Uploaded documents</h3>
      {may.uploadDocuments ? (
        <div className={styles.actions}>
          <UploadDocument visitId={uploadVisitId} listKeys={[key]} online={online} />
        </div>
      ) : null}
      {may.readDocuments ? (
        <Loaded query={documents} label="Loading documents">
          {(data) => (
            <DocumentList
              documents={data.items}
              listKeys={[key]}
              mayCorrect={may.uploadDocuments}
              online={online}
            />
          )}
        </Loaded>
      ) : (
        <p className={styles.help}>Seeing uploaded documents needs the documents permission.</p>
      )}
    </section>
  );
}

/** The workspace's documents slot: this visit's sheets and letters, and the patient's uploads. */
export function ConsultationDocumentsPanel({
  visitId,
  patientId,
  consultationId,
  visitOpen,
  visitStartedAt,
  diagnoses,
  may,
}: {
  visitId: string;
  patientId: string;
  consultationId: string | null;
  visitOpen: boolean;
  visitStartedAt: string;
  diagnoses: readonly DiagnosisChoice[];
  may: DocumentPermissions;
}) {
  const online = useOnline();
  const sickSheetsKey = documentKeys.visitSickSheets(visitId);
  const referralsKey = documentKeys.visitReferrals(visitId);
  const sickSheets = useQuery({ ...visitSickSheetsQuery(visitId), enabled: may.readIssued });
  const referrals = useQuery({ ...visitReferralsQuery(visitId), enabled: may.readIssued });
  const listKeys = [
    sickSheetsKey,
    referralsKey,
    documentKeys.patientSickSheets(patientId),
    documentKeys.patientReferrals(patientId),
  ];
  const writeDisabled = !online || !visitOpen;
  return (
    <section className={styles.panel} aria-labelledby="documents-heading">
      <h2 id="documents-heading" className={styles.heading}>
        Documents
      </h2>
      {may.readIssued ? (
        <IssuedSection
          heading="Sick sheets and referral letters"
          sickSheetsKey={sickSheetsKey}
          referralsKey={referralsKey}
          sickSheets={sickSheets}
          referrals={referrals}
          may={may}
          online={online}
          actions={
            may.issueSickSheet || may.writeReferral ? (
              <>
                <div className={styles.actions}>
                  {may.issueSickSheet ? (
                    <IssueSickSheet
                      visitId={visitId}
                      consultationId={consultationId}
                      visitStartedAt={visitStartedAt}
                      diagnoses={diagnoses}
                      listKeys={listKeys}
                      isDisabled={writeDisabled}
                    />
                  ) : null}
                  {may.writeReferral ? (
                    <WriteReferral
                      visitId={visitId}
                      consultationId={consultationId}
                      listKeys={listKeys}
                      isDisabled={writeDisabled}
                    />
                  ) : null}
                </div>
                {!online ? (
                  <p className={styles.help}>Issuing needs a connection to Wodi.</p>
                ) : !visitOpen ? (
                  <p className={styles.help}>
                    This visit is closed, so nothing new can be issued in it.
                  </p>
                ) : null}
              </>
            ) : null
          }
        />
      ) : null}
      {may.readDocuments || may.uploadDocuments ? (
        <DocumentsSection
          patientId={patientId}
          uploadVisitId={visitOpen ? visitId : null}
          may={may}
          online={online}
        />
      ) : null}
    </section>
  );
}

/** The patient record's documents: every visit's sheets and letters, and uploads. */
export function PatientDocumentsPanel({
  patientId,
  openVisitId,
  may,
}: {
  patientId: string;
  /** The patient's open visit, where uploads go; null when none is open. */
  openVisitId: string | null;
  may: DocumentPermissions;
}) {
  const online = useOnline();
  const sickSheetsKey = documentKeys.patientSickSheets(patientId);
  const referralsKey = documentKeys.patientReferrals(patientId);
  const sickSheets = useQuery({ ...patientSickSheetsQuery(patientId), enabled: may.readIssued });
  const referrals = useQuery({ ...patientReferralsQuery(patientId), enabled: may.readIssued });
  if (!may.readIssued && !may.readDocuments && !may.uploadDocuments) return null;
  return (
    <section className={styles.panel} aria-labelledby="patient-documents-heading">
      <h2 id="patient-documents-heading" className={styles.heading}>
        Documents
      </h2>
      {may.readIssued ? (
        <IssuedSection
          heading="Sick sheets and referral letters"
          sickSheetsKey={sickSheetsKey}
          referralsKey={referralsKey}
          sickSheets={sickSheets}
          referrals={referrals}
          may={may}
          online={online}
          actions={null}
        />
      ) : null}
      <DocumentsSection
        patientId={patientId}
        uploadVisitId={openVisitId}
        may={may}
        online={online}
      />
    </section>
  );
}
